/**
 * @file src/core/audio.js
 * ctx.audio — phát âm thanh của tiết. KHÔNG có trong three-lessons (bản cũ chưa dùng
 * audio), viết mới cho core.
 *
 * Vì sao nằm ở CORE chứ không để mỗi tiết tự làm:
 *   1. AGENT.md mục 4.4: nút âm thanh và thanh âm lượng là của SHELL. Nếu tiết tự
 *      tạo AudioContext thì nó phải tự nghe settings, và sẽ có tiết quên → tắt tiếng
 *      mà vẫn có tiếng. Ở đây nối một lần, mọi tiết được đúng.
 *   2. Chính sách autoplay: trình duyệt CHẶN AudioContext cho tới khi có tương tác
 *      của người dùng. Xử lý đúng chuyện này khá lắt nhắt (xem unlock() bên dưới) —
 *      không nên bắt mỗi dev tiết tự làm lại.
 *   3. Một AudioContext dùng chung cho cả tiết. Chrome giới hạn số context mỗi trang,
 *      mỗi tiết tự tạo vài cái là cạn.
 *
 * Tiết chỉ cần: await ctx.audio.play('audio.intro')
 */

/**
 * @typedef {Object} AudioApi
 * @property {(key: string, opts?: {loop?: boolean, gain?: number}) => Promise<void>} play
 *   Phát. Trả về Promise resolve NGAY khi đã bắt đầu phát (không chờ phát xong).
 *   Nếu trình duyệt còn chặn autoplay thì tự phát lại sau tương tác đầu tiên.
 * @property {(key: string) => void} stop
 * @property {() => void} stopAll
 * @property {() => boolean} isPlaying
 * @property {() => void} dispose
 */

/**
 * @param {Object} o
 * @param {import('./settings.js').Settings} o.settings
 * @param {{ audio: (key: string) => Promise<AudioBuffer> }} o.assets
 * @param {(code: string, opt?: any) => void} o.reportError
 * @returns {AudioApi}
 */
export function createAudio(o) {
  /** @type {AudioContext | null} */
  let ac = null;
  /** @type {GainNode | null} */
  let master = null;
  /** @type {Map<string, AudioBufferSourceNode>} */
  const playing = new Map();
  /** Việc chờ tương tác của người dùng để được phát. @type {Array<() => void>} */
  let pending = [];
  /** @type {(() => void) | null} */
  let removeUnlock = null;
  let disposed = false;

  /** Mức gain thực tế: tắt tiếng thì 0, nhưng KHÔNG xoá mức âm lượng đã chọn. */
  const wantedGain = () =>
    o.settings.get('sound') ? Math.max(0, Math.min(1, Number(o.settings.get('volume') ?? 1))) : 0;

  function ensureContext() {
    if (ac) return ac;
    const AC = window.AudioContext ?? /** @type {any} */ (window).webkitAudioContext;
    if (!AC) return null;
    ac = new AC();
    master = ac.createGain();
    master.gain.value = wantedGain();
    master.connect(ac.destination);
    return ac;
  }

  // Đổi âm lượng/tắt tiếng áp dụng NGAY cho cả tiếng đang phát.
  // rampTo thay vì gán thẳng — gán thẳng gây tiếng "tách" ở loa.
  const applyGain = () => {
    if (!ac || !master) return;
    master.gain.cancelScheduledValues(ac.currentTime);
    master.gain.setTargetAtTime(wantedGain(), ac.currentTime, 0.02);
  };
  const offSound = o.settings.onChange('sound', applyGain);
  const offVolume = o.settings.onChange('volume', applyGain);

  /**
   * Chính sách autoplay: AudioContext mới tạo ở trạng thái 'suspended' cho tới khi
   * người dùng tương tác. resume() gọi ngoài cử chỉ người dùng sẽ bị từ chối im lặng,
   * nên phải hoãn việc phát lại tới lần bấm/chạm/nhấn phím đầu tiên.
   */
  function unlock() {
    if (removeUnlock || disposed) return;
    const go = () => {
      removeUnlock?.();
      removeUnlock = null;
      ac?.resume().then(
        () => {
          const q = pending;
          pending = [];
          for (const fn of q) fn();
        },
        () => {
          pending = [];
        },
      );
    };
    const opts = { once: true, passive: true };
    window.addEventListener('pointerdown', go, opts);
    window.addEventListener('keydown', go, opts);
    window.addEventListener('touchstart', go, opts);
    removeUnlock = () => {
      window.removeEventListener('pointerdown', go);
      window.removeEventListener('keydown', go);
      window.removeEventListener('touchstart', go);
    };
  }

  /** @type {AudioApi} */
  const api = {
    async play(key, opts = {}) {
      if (disposed) return;
      const context = ensureContext();
      if (!context || !master) return; // không có Web Audio → bỏ qua, KHÔNG báo lỗi

      /** @type {AudioBuffer} */
      let buf;
      try {
        buf = await o.assets.audio(key);
      } catch {
        // Thiếu/không giải mã được audio KHÔNG được làm chết bài học.
        o.reportError('ASSET_404', {
          fatal: false,
          retryable: false,
          messageKey: 'err.assetFailed',
          detail: key,
        });
        return;
      }
      if (disposed) return;

      const start = () => {
        if (disposed || !ac || !master) return;
        api.stop(key); // không cho một khoá phát chồng lên chính nó
        const src = ac.createBufferSource();
        src.buffer = buf;
        src.loop = Boolean(opts.loop);
        if (opts.gain !== undefined) {
          const g = ac.createGain();
          g.gain.value = Math.max(0, Math.min(1, opts.gain));
          src.connect(g).connect(master);
        } else {
          src.connect(master);
        }
        src.onended = () => {
          if (playing.get(key) === src) playing.delete(key);
        };
        src.start();
        playing.set(key, src);
      };

      if (context.state === 'suspended') {
        pending.push(start);
        unlock();
        // Vẫn thử resume: nếu đang trong một cử chỉ người dùng thì sẽ chạy ngay.
        context.resume().then(
          () => {
            const q = pending;
            pending = [];
            for (const fn of q) fn();
          },
          () => {},
        );
        return;
      }
      start();
    },

    stop(key) {
      const src = playing.get(key);
      if (!src) return;
      try {
        src.onended = null;
        src.stop();
      } catch {
        /* đã dừng rồi — bỏ qua */
      }
      playing.delete(key);
    },

    stopAll() {
      for (const key of [...playing.keys()]) api.stop(key);
      pending = [];
    },

    isPlaying() {
      return playing.size > 0;
    },

    dispose() {
      disposed = true;
      api.stopAll();
      offSound();
      offVolume();
      removeUnlock?.();
      removeUnlock = null;
      ac?.close().catch(() => {});
      ac = null;
      master = null;
    },
  };

  return api;
}
